import { useCallback, useContext, useMemo } from 'react'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { ConnectedUserContext } from '../../../../misc/auth.utilities'
import { Filters } from '../../../../model/Filters'
import { getNextPageParam } from '../../../../misc/query.utilities'
import { isAdmin } from '../../../../misc/user.utilities'
import { UserFriendlyError } from '../../../../model/Error'
import { useFlatPaginatedData } from '../../../utils/query/useFlatPaginatedData.hook'
import { usePaginationState } from '../../../utils/query'
import { startingPage } from '../../../../constants'
import { getOrganizationsInterop } from '../../../../data/admin'
import { organizationInteropKeys } from './organizationInterop.keys'
import { OrganizationInterop } from '../../../../model/OrganizationInterop'
import { PaginatedList } from '../../../../model/Pagination'

interface GetInfiniteOrganizationsInteropParams {
  filters?: Filters
  limit?: number
  enabled?: boolean
}

export const useGetOrganizationsInterop = ({
  filters,
  limit,
  enabled = true,
}: GetInfiniteOrganizationsInteropParams) => {
  const queryKey = useMemo(() => organizationInteropKeys.list(filters), [filters])

  const { currentUser } = useContext(ConnectedUserContext)

  const query = useInfiniteQuery<PaginatedList<OrganizationInterop>>({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const { data, ok } = await getOrganizationsInterop(
        pageParam as number,
        filters,
        signal,
        limit,
      )
      if (!data || !ok) {
        throw new UserFriendlyError(
          'error',
          `Impossible de recuperer la liste des organisations interop`,
          `Une erreur technique s'est produite`,
        )
      }
      return data
    },
    enabled: isAdmin(currentUser) && enabled,
    initialPageParam: startingPage,
    getNextPageParam,
  })

  const queryClient = useQueryClient()
  const organizationsInteropList = useFlatPaginatedData<OrganizationInterop>(query.data)
  const paginationState = usePaginationState(query.data)

  const cancelPendingQuery = useCallback(() => {
    queryClient.cancelQueries({ queryKey })
  }, [queryClient, queryKey])

  return { query, organizationsInteropList, paginationState, cancelPendingQuery }
}
